import axios from 'axios';
import { db, getPendingSyncs, markSyncComplete, markSyncFailed } from './db';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

export class SyncManager {
  private isSyncing = false;
  private syncInterval: NodeJS.Timeout | null = null;

  startAutoSync(intervalMs: number = 30000) {
    // Sync every 30 seconds when online
    this.syncInterval = setInterval(() => {
      this.syncPendingChanges();
    }, intervalMs);
  }

  stopAutoSync() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }

  async syncPendingChanges(): Promise<{ success: number; failed: number }> {
    if (this.isSyncing) {
      console.log('Sync already in progress, skipping...');
      return { success: 0, failed: 0 };
    }

    this.isSyncing = true;
    let successCount = 0;
    let failedCount = 0;

    try {
      const pending = await getPendingSyncs();
      console.log(`Syncing ${pending.length} pending changes...`);

      for (const item of pending) {
        try {
          await this.syncItem(item);
          await markSyncComplete(item.id!);
          successCount++;
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          await markSyncFailed(item.id!, errorMessage);
          failedCount++;
          console.error(`Failed to sync item ${item.id}:`, error);
        }
      }

      if (successCount > 0) {
        console.log(`Successfully synced ${successCount} items`);
      }
      if (failedCount > 0) {
        console.log(`Failed to sync ${failedCount} items`);
      }
    } finally {
      this.isSyncing = false;
    }

    return { success: successCount, failed: failedCount };
  }

  private async syncItem(item: any) {
    const token = localStorage.getItem('token');
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };

    const endpoint = this.getEndpoint(item.entity, item.entityId);

    switch (item.action) {
      case 'create':
        await axios.post(endpoint, item.data, { headers });
        break;
      
      case 'update':
        await axios.put(endpoint, item.data, { headers });
        break;
      
      case 'delete':
        await axios.delete(endpoint, { headers });
        break;
    }
  }

  private getEndpoint(entity: string, entityId?: string): string {
    switch (entity) {
      case 'patient':
        return entityId 
          ? `${API_BASE_URL}/patients/${entityId}`
          : `${API_BASE_URL}/patients`;
      
      case 'alert':
        return entityId
          ? `${API_BASE_URL}/alerts/${entityId}`
          : `${API_BASE_URL}/alerts`;
      
      case 'handoff':
        return entityId
          ? `${API_BASE_URL}/handoffs/${entityId}`
          : `${API_BASE_URL}/handoffs`;
      
      default:
        throw new Error(`Unknown entity type: ${entity}`);
    }
  }
}

// Create singleton instance
export const syncManager = new SyncManager();
